import { Fragment } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Icon } from '@/icons';
import { usePremium, useSession, useSettingsSections } from '@/hooks';
import { softControlMotion } from '@/theme/interactions';
import { sectionIcons } from './sectionIcons';

const Aside = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
`;

const Head = styled.div`
  margin-bottom: 12px;
`;

const HeadTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const HeadBlurb = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const NavLink = styled(Link)<{ $active: boolean; $collapsed: boolean }>`
  ${softControlMotion}
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => ($collapsed ? '0' : '12px')};
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  min-height: 44px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: ${({ theme, $active }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  background: ${({ theme, $active }) => ($active ? theme.color.surface : 'transparent')};
  font-weight: ${({ $active }) => ($active ? 600 : 'inherit')};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

/* 1.27.70: sentence-case group headings from the owner's concept (which drew them
   in capitals; the no-capitals rule wins). Muted and small, so the links lead. */
const GroupLabel = styled.p`
  margin: 16px 0 4px;
  padding: 0 12px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

const GroupRule = styled.hr`
  margin: 10px 12px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Promo = styled.div`
  margin-top: auto;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  /* De-emphasised to a void surface + hairline (was a loud teal-gradient block that
     out-shouted the real settings actions). The inner PromoLink stays the one teal CTA. */
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PromoHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* The crown is the one warm mark on the card: the supporter hue, not the accent. */
  > svg {
    flex: none;
    color: ${({ theme }) => theme.color.supporter};
  }
`;

const PromoText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
`;

const PromoBlurb = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
`;

const PromoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  text-decoration: none;
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 14%, transparent);
  color: ${({ theme }) => theme.color.accentCyan};
  cursor: pointer;
  transition:
    transform 80ms ease,
    box-shadow 160ms ease,
    opacity 160ms ease;
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

export interface SettingsSidebarProps {
  activeId: string;
  collapsed?: boolean;
}

export function SettingsSidebar({ activeId, collapsed = false }: SettingsSidebarProps) {
  const { sections, groupLabel } = useSettingsSections();
  const { isPremium } = useSession();
  const pro = usePremium().tiers.find((t) => t.id === 'pro');
  return (
    <Aside id="settings-sidebar" aria-label="Settings sections">
      {!collapsed && (
        <Head>
          <HeadTitle>Settings</HeadTitle>
          <HeadBlurb>Manage your preferences and personalize your experience.</HeadBlurb>
        </Head>
      )}
      {sections.map((s, i) => {
        const group = s.group && s.group !== sections[i - 1]?.group ? s.group : null;
        return (
          <Fragment key={s.id}>
            {group &&
              (collapsed ? (
                <GroupRule aria-hidden="true" />
              ) : (
                <GroupLabel>{groupLabel(group)}</GroupLabel>
              ))}
            <NavLink
              to={`/settings/${s.id}`}
              replace
              $active={s.id === activeId}
              $collapsed={collapsed}
              aria-label={s.label}
              title={collapsed ? s.label : undefined}
              aria-current={s.id === activeId ? 'page' : undefined}
            >
              <Icon name={sectionIcons[s.id] ?? 'settings'} size={18} />
              {!collapsed && s.label}
            </NavLink>
          </Fragment>
        );
      })}
      {!collapsed && (
        <Promo>
          <PromoHead>
            <Icon name="crown" size={18} />
            <PromoText>{isPremium ? 'TruXSocial Premium' : 'Upgrade to Premium'}</PromoText>
          </PromoHead>
          {/* Copy from core/data/premium.ts: the Pro tagline and its last promise. */}
          {pro && (
            <PromoBlurb>
              {pro.tagline}. {pro.benefits[pro.benefits.length - 1]}.
            </PromoBlurb>
          )}
          <PromoLink to="/premium">
            {isPremium ? 'Manage Premium' : 'Upgrade now'}
            <Icon name="arrow-right" size={14} />
          </PromoLink>
        </Promo>
      )}
    </Aside>
  );
}
